'use client';

import { useInsertionEffect, useMemo, useRef, type ReactNode } from 'react';
import {
    EnterApiProvider,
    getSwimLaneRequestHeaders,
    type EnterApiConfig,
    type EnterApiHttpEvents,
    type ExtraHeaders,
    type MaybePromise,
} from '@enter/api';
import { useEnterAuthToken, useEnterLogin } from '@enter/auth';
import {
    getFallbackBackendSwimLane,
    getSiteOrigin,
    getSocketProjectBaseUrl,
    isPersistedSwimLaneDisabled,
} from '@/lib/app-origin';

export interface ApiProviderProps {
    /** 应用子树。 */
    children: ReactNode;
    /** App 按区域注入的归因请求头，每次请求发出时求值；要不要等待由 App 决定，API 层不内置。 */
    getAttributionHeaders: () => MaybePromise<ExtraHeaders>;
    /** App 注入的传输层事件回调；API 不 import 业务模块决定 UI。 */
    httpEvents?: EnterApiHttpEvents;
}

export function ApiProvider({ children, getAttributionHeaders, httpEvents }: ApiProviderProps) {
    const { getAccessToken } = useEnterAuthToken();
    const { loginWithRedirect } = useEnterLogin();
    // 回调经 ref 读取最新值，客户端配置不随语言、SDK 状态或回调身份重建。
    // 在 insertion 阶段同步，早于子树任何 effect 发出的请求，且不在 render 中写 ref。
    const latest = useRef({ getAccessToken, loginWithRedirect, getAttributionHeaders, httpEvents });
    useInsertionEffect(() => {
        latest.current = { getAccessToken, loginWithRedirect, getAttributionHeaders, httpEvents };
    });

    const config = useMemo<EnterApiConfig>(() => ({
        apiBaseUrl: process.env.NEXT_PUBLIC_API_BASE_URL ?? '',
        publicSiteBaseUrl: getSiteOrigin(),
        socketProjectBaseUrl: getSocketProjectBaseUrl(),
        getExtraHeaders: async () => ({
            ...getSwimLaneRequestHeaders({
                storage: isPersistedSwimLaneDisabled() ? null : undefined,
                fallbackBackendSwimLane: getFallbackBackendSwimLane(),
            }),
            ...toRecord(await latest.current.getAttributionHeaders()),
        }),
        httpEvents: {
            onRetryAfter: (event) => latest.current.httpEvents?.onRetryAfter?.(event),
            onRetryExhausted: (event) => latest.current.httpEvents?.onRetryExhausted?.(event),
            onFinalHttpError: (error) => latest.current.httpEvents?.onFinalHttpError?.(error),
        },
        authAdapter: {
            getAccessToken: () => latest.current.getAccessToken(),
            onAuthRequired: async () => {
                await latest.current.loginWithRedirect({
                    appState: {
                        returnTo: window.location.pathname + window.location.search + window.location.hash,
                    },
                });
            },
        },
    }), []);

    return <EnterApiProvider config={config}>{children}</EnterApiProvider>;
}

function toRecord(headers: ExtraHeaders | null | undefined): Record<string, string> {
    if (!headers) return {};
    if (headers instanceof Headers) return Object.fromEntries(headers.entries());
    return Object.fromEntries(
        Object.entries(headers)
            .filter((entry): entry is [string, string | number | boolean] => entry[1] !== null && entry[1] !== undefined)
            .map(([key, value]) => [key, String(value)]),
    );
}
